import { getInactiveStepIds } from 'features/libra/workflow/inactiveSteps'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { createContext, FC, useContext, useMemo } from 'react'

import { LibraTicket } from './libraTicketsApi'

export interface TicketStepContextType {
  activeStepId: string
  isActiveStep: boolean
  /** The step is routed past by the ticket's field values. */
  isStepInactive: boolean
  hasMoreSteps: boolean
  isStepComplete: boolean
  isWorkflowComplete: boolean
  stepsComplete: [number, number]
}

const TicketStepContext = createContext<TicketStepContextType>({
  activeStepId: '',
  isActiveStep: false,
  isStepInactive: false,
  hasMoreSteps: false,
  isStepComplete: false,
  isWorkflowComplete: false,
  stepsComplete: [0, 0],
})

export const TicketStepContextProvider: FC<{
  ticket: LibraTicket
  activeStepId?: string
  children: React.ReactNode
}> = ({ children, ticket, activeStepId = ticket.stepId }) => {
  const workflow = useWorkflow()
  return (
    <TicketStepContext.Provider
      value={useMemo(() => {
        const stepIdx = workflow.stepIndices.get(activeStepId) ?? -1
        const ticketStepIdx = workflow.stepIndices.get(ticket.stepId) ?? -1
        return {
          activeStepId,
          isActiveStep:
            ticket.stepId === activeStepId && !ticket.workflowComplete,
          isStepInactive: getInactiveStepIds(workflow, ticket.fieldValues).has(
            activeStepId
          ),
          hasMoreSteps: stepIdx < workflow.steps.length - 1,
          isStepComplete: stepIdx < ticketStepIdx || ticket.workflowComplete,
          isWorkflowComplete: ticket.workflowComplete ?? false,
          stepsComplete: [Math.max(ticketStepIdx, 0), workflow.steps.length],
        }
      }, [
        activeStepId,
        ticket.stepId,
        ticket.fieldValues,
        ticket.workflowComplete,
        workflow,
      ])}>
      {children}
    </TicketStepContext.Provider>
  )
}

export const useTicketStepContext = () => {
  return useContext(TicketStepContext)
}

export const useActiveStepId = (): string => {
  return useContext(TicketStepContext).activeStepId
}

/**
 * Overrides `activeStepId` for all descendants while preserving the rest
 * of the parent `TicketStepContext` values.  Used by NiiReview to scope
 * each accordion panel to the correct sub-step.
 */
export const ActiveStepIdOverride: FC<{
  stepId: string
  children: React.ReactNode
}> = ({ stepId, children }) => {
  const parent = useTicketStepContext()
  const value = useMemo(
    () => ({ ...parent, activeStepId: stepId }),
    [parent, stepId]
  )
  return (
    <TicketStepContext.Provider value={value}>
      {children}
    </TicketStepContext.Provider>
  )
}
